// Hook wrapping the WorkOrderV2 ViewSet endpoints. Owns list state,
// loading/error flags, refresh, and optimistic updates on mutations.
import { useCallback, useEffect, useRef, useState } from 'react';
import { v2 } from '../../../../services/api';

export function useWOv2(initialParams = {}) {
  const [workOrders, setWorkOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [params, setParams] = useState(initialParams);

  const mountedRef = useRef(true);
  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  const fetchList = useCallback(async (overrideParams) => {
    setLoading(true);
    setError(null);
    try {
      const res = await v2.workOrders.list(overrideParams || params);
      const data = res.data?.results || res.data || [];
      if (mountedRef.current) setWorkOrders(Array.isArray(data) ? data : []);
      return data;
    } catch (err) {
      console.error('useWOv2: list failed', err);
      if (mountedRef.current) setError(err);
      return null;
    } finally {
      if (mountedRef.current) setLoading(false);
    }
  }, [params]);

  useEffect(() => {
    fetchList();
  }, [fetchList]);

  const refresh = useCallback(() => fetchList(), [fetchList]);

  // ── Optimistic mutation helper ────────────────────────────────────
  const replaceLocal = useCallback((id, updater) => {
    setWorkOrders((prev) =>
      prev.map((wo) => (wo.id === id ? { ...wo, ...updater(wo) } : wo))
    );
  }, []);

  // ── Mutating actions ──────────────────────────────────────────────
  const startStep = useCallback(async (woId, stepId) => {
    try {
      const res = await v2.workOrders.startStep(woId, stepId);
      replaceLocal(woId, () => ({ status: 'in_progress' }));
      return res.data;
    } catch (err) {
      console.error('useWOv2: startStep failed', err);
      throw err;
    }
  }, [replaceLocal]);

  const completeStep = useCallback(async (woId, stepId, payload) => {
    try {
      const res = await v2.workOrders.completeStep(woId, stepId, payload);
      // Refresh single WO so steps/status are accurate
      try {
        const detail = await v2.workOrders.retrieve(woId);
        replaceLocal(woId, () => detail.data);
      } catch (_) {
        // ignore — list refresh will pick it up
      }
      return res.data;
    } catch (err) {
      console.error('useWOv2: completeStep failed', err);
      throw err;
    }
  }, [replaceLocal]);

  const scrap = useCallback(async (woId, payload) => {
    const res = await v2.workOrders.scrap(woId, payload);
    return res.data;
  }, []);

  // Use a ref so we can read the latest workOrders synchronously when we
  // need to capture pre-mutation state for optimistic rollback. Keeping it
  // in a ref avoids stale-closure issues in tests that drive the hook
  // through multiple state transitions.
  const workOrdersRef = useRef([]);
  useEffect(() => { workOrdersRef.current = workOrders; }, [workOrders]);

  const cancel = useCallback(async (woId, payload = {}) => {
    const existing = workOrdersRef.current.find((wo) => wo.id === woId);
    const previousStatus = existing ? existing.status : null;
    // Optimistic flip
    setWorkOrders((prev) => prev.map((wo) =>
      wo.id === woId ? { ...wo, status: 'cancelled' } : wo
    ));
    try {
      const res = await v2.workOrders.cancel(woId, payload);
      replaceLocal(woId, () => res.data);
      return res.data;
    } catch (err) {
      if (previousStatus != null && previousStatus !== 'cancelled') {
        replaceLocal(woId, () => ({ status: previousStatus }));
      }
      console.error('useWOv2: cancel failed', err);
      throw err;
    }
  }, [replaceLocal]);

  const adjust = useCallback(async (woId, payload) => {
    const res = await v2.workOrders.adjust(woId, payload);
    return res.data;
  }, []);

  return {
    workOrders,
    loading,
    error,
    params,
    setParams,
    refresh,
    startStep,
    completeStep,
    scrap,
    cancel,
    adjust,
  };
}

export default useWOv2;
